import React, { useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Typography,
  Container,
  Pagination,
  Skeleton,
  Box,
} from "@mui/material";
import { Breadcrumb } from "../../components/common/Breadcrumb";
import { SEO } from "../../components/common/SEO";
import { Blog } from "../../redux/types/blogTypes";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { fetchBlogs } from "../../redux/actions/blogActions";
import ReactRouterLink from "../../components/common/ReactRouterLink";
import { DocumentSnapshot } from "firebase/firestore";

const PAGE_SIZE = 6;

const BlogPage: React.FC = () => {
  const dispatch = useDispatch();
  const { loading, blogsData, error } = useSelector(
    (state: RootState) => state.blogs
  );

  useEffect(() => {
    dispatch(
      fetchBlogs(
        "blogs",
        "createdTime",
        undefined,
        undefined,
        undefined,
        PAGE_SIZE
      )
    );
  }, [dispatch]);

  const [currentPage, setCurrentPage] = useState(1);

  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    if (value !== currentPage) {
      const startAfterDoc = value > currentPage ? blogsData.lastDoc : undefined;
      const endBeforeDoc = value < currentPage ? blogsData.firstDoc : undefined;
      dispatch(
        fetchBlogs(
          "blogs",
          "createdTime",
          value < currentPage ? "prev" : "next",
          startAfterDoc,
          endBeforeDoc,
          PAGE_SIZE
        )
      );
      // Scroll to the blog content after the new content is loaded
      const blogContent = document.getElementById("blogContent");
      if (blogContent) {
        let yOffset =
          blogContent.getBoundingClientRect().top + window.pageYOffset;

        // Adjust scroll position for mobile
        if (window.innerWidth <= 600) {
          yOffset -= 55; // Adjust this value as needed
        }

        window.scrollTo({ top: yOffset });
      }
    }

    setCurrentPage(value);
  };

  return (
    <>
      <SEO
        title="Blog • FiddlePiddle • Toys Marketplace"
        description={
          "Our blog is dedicated to bringing you the latest news, insights, trends, and stories from the vibrant world of toys.Z"
        }
        keywordsContent="Blog, toys marketplace, marketplace for toys, fiddlepiddle"
      />
      <Breadcrumb
        items={[
          { text: "Home", url: "/" },
          { text: "Blog", url: "/blog" },
        ]}
      />
      <Container maxWidth="lg" sx={{ mb: 10 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Welcome to Our Toy Blog
        </Typography>
        <Typography variant="body1" paragraph>
          Our blog is dedicated to bringing you the latest news, insights,
          trends, and stories from the vibrant world of toys.
        </Typography>
        <Grid container spacing={3} id="blogContent">
          {loading
            ? Array.from({ length: 6 }).map((_, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Skeleton
                    variant="rectangular"
                    height={300}
                    animation="wave"
                    style={{ borderRadius: 16 }}
                  />
                </Grid>
              ))
            : blogsData.blogs.map((blog, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card sx={{ height: "100%" }}>
                    <ReactRouterLink
                      to={blog.blogUrl}
                      isLinkUnderline={false}
                      linkElement={
                        <CardActionArea>
                          <CardMedia
                            component="img"
                            height="200"
                            image={"/blog-default.jpg"}
                            alt="Blog Image"
                          />
                          <CardContent>
                            <Typography variant="h6" component="h2">
                              {blog.blogTitle}
                            </Typography>
                          </CardContent>
                        </CardActionArea>
                      }
                    />
                  </Card>
                </Grid>
              ))}
        </Grid>

        <Pagination
          count={Math.ceil(blogsData.totalRecords / PAGE_SIZE)}
          page={currentPage}
          onChange={handleChange}
          variant="outlined"
          shape="rounded"
          sx={{
            mt: 4,
            justifyContent: "center",
            "& .MuiPaginationItem-page": {
              display: "none",
            },
          }}
          style={{ float: "right" }}
        />
      </Container>
    </>
  );
};

export default BlogPage;
